import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Backdrop, Box, InputBase, Portal, Typography } from '@mui/material';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { SearchIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { DocumentsSubtabRouteParam } from 'constants/loanApplication';
import useAutofocusSearchOnOpen from 'hooks/useAutofocusSearchOnOpen';

import { ENTITY_TYPE_HREF } from '../tabs/borrowers/EditEntityModal/EditEntityModal.constants';
import {
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_ACTION_TYPE,
} from '../tabs/financialInfo/FinancialInfo.constants';
import {
  ENTITY_TYPE_HREF_MAP,
  getSectionToRedirectByEntityType,
} from '../tabs/propertyAndLoan/EditModal/EditModal.constants';

import SearchResults from './components/SearchResults';
import {
  useFinancialInfoPrefetch,
  useKeyboardNavigation,
  useRecentSearches,
  useSearchResults,
} from './hooks';
import {
  LOAN_ACTION_TRIGGER_EVENT,
  SEARCH_INPUT_PLACEHOLDER,
  SearchCategory,
} from './UniversalSearch.constants';
import {
  backdropStyles,
  escHintStyles,
  footerHintStyles,
  footerKeyStyles,
  footerStyles,
  paletteContainerStyles,
  searchIconStyles,
  searchInputStyles,
  searchInputWrapperStyles,
} from './UniversalSearch.styles';
import { focusAndHighlightField } from './UniversalSearch.utils';

const UniversalSearch = ({ initialQuery = '', isOpen, onClose }) => {
  const history = useHistory();
  const { applicationId } = useParams();
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(initialQuery);
  const { prefetchFinancialInfo } = useFinancialInfoPrefetch();
  const { recentItems, addRecentItem, clearRecentItems } = useRecentSearches();

  const {
    query,
    setQuery,
    onQueryChange,
    groupedResults,
    flatResults,
    flatSuggestedItems,
    shortcutEntries,
    suggestedItems,
  } = useSearchResults({ initialQuery });

  const hasQuery = !!query?.trim();
  const navigableItems = hasQuery ? flatResults : flatSuggestedItems;

  const handleSelect = useCallback(
    item => {
      onClose();
      addRecentItem(item);

      if (item.category === SearchCategory.NAVIGATION) {
        const pathParams = {
          [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
          [LOANS_ROUTING_PARAMS.tab]: item.tabRoute,
        };

        if (item.subtabRoute) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.subtabRoute;
        }

        if (item.financialTablePath) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.financialTablePath;
          pathParams[LOANS_ROUTING_PARAMS.entityId] = ALL_BORROWERS_PATH;
          pathParams[LOANS_ROUTING_PARAMS.section] = FINANCIAL_INFO_ACTION_TYPE.EDIT;
          pathParams[LOANS_ROUTING_PARAMS.subsection] = item.financialRecordId;
        }

        if (item.borrowerEntityType) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.borrowerEntityType;
          pathParams[LOANS_ROUTING_PARAMS.entityId] = item.borrowerEntityId;
          pathParams[LOANS_ROUTING_PARAMS.section] = item.borrowerDefaultSection;
        }

        if (!item.borrowerEntityType && item.entityType) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.entityType;
          const defaultSection = getSectionToRedirectByEntityType({ entityType: item.entityType });
          const resolvedSection = item.sectionHref ?? item.entityId ?? defaultSection;

          if (resolvedSection) {
            pathParams[LOANS_ROUTING_PARAMS.entityId] = resolvedSection;
          }
        }

        if (!item.borrowerEntityType && !item.entityType && item.entityId) {
          pathParams[LOANS_ROUTING_PARAMS.entityId] = item.entityId;
        }

        if (!item.borrowerEntityType && !item.entityType && item.sectionHref) {
          pathParams[LOANS_ROUTING_PARAMS.entityId] = item.sectionHref;
        }

        if (item.checklistId) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.checklistId;
        }

        if (item.loanClosingSection) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.loanClosingSection;
        }

        if (item.section) {
          pathParams[LOANS_ROUTING_PARAMS.section] = item.section;
        }

        if (item.subsection) {
          pathParams[LOANS_ROUTING_PARAMS.subsection] = item.subsection;
        }

        history.push(LoansPaths.applicationManagement.generatePath(pathParams));

        return;
      }

      if (item.category === SearchCategory.FIELDS) {
        const pathParams = {
          [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
          [LOANS_ROUTING_PARAMS.tab]: item.tabRoute,
        };

        if (item.entityType && item.sectionHref) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.entityType;
          pathParams[LOANS_ROUTING_PARAMS.entityId] = item.sectionHref;
        }

        if (item.loanClosingSection) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.loanClosingSection;
        }

        history.push(LoansPaths.applicationManagement.generatePath(pathParams));

        focusAndHighlightField({ fieldId: item.fieldId });

        return;
      }

      if (item.category === SearchCategory.CONTACTS) {
        history.push(
          LoansPaths.applicationManagement.generatePath({
            [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
            [LOANS_ROUTING_PARAMS.tab]: item.tabRoute,
            [LOANS_ROUTING_PARAMS.subtab]: ENTITY_TYPE_HREF_MAP.CONTACTS,
            [LOANS_ROUTING_PARAMS.entityId]: item.contactId,
          }),
        );

        return;
      }

      if (item.category === SearchCategory.ACTIONS) {
        document.dispatchEvent(
          new CustomEvent(LOAN_ACTION_TRIGGER_EVENT, {
            detail: { actionId: item.actionId, actionName: item.actionName },
          }),
        );

        return;
      }

      if (item.category === SearchCategory.DOCUMENTS) {
        const pathParams = {
          [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
          [LOANS_ROUTING_PARAMS.tab]: item.tabRoute,
          [LOANS_ROUTING_PARAMS.subtab]: item.subtabRoute || DocumentsSubtabRouteParam.DOCUMENTS,
        };

        const path = LoansPaths.applicationManagement.generatePath(pathParams);

        history.push(item.documentId ? `${path}?documentId=${item.documentId}` : path);

        return;
      }

      if (item.category === SearchCategory.RECORDS) {
        const pathParams = {
          [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
          [LOANS_ROUTING_PARAMS.tab]: item.tabRoute,
        };

        if (item.borrowerSection) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = ENTITY_TYPE_HREF.BORROWER;
          pathParams[LOANS_ROUTING_PARAMS.entityId] = item.recordId;
          pathParams[LOANS_ROUTING_PARAMS.section] = item.borrowerSection;
        }

        if (item.recordType === 'borrower' && !item.borrowerSection) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = ENTITY_TYPE_HREF.BORROWER;
          pathParams[LOANS_ROUTING_PARAMS.entityId] = item.recordId;
        }

        if (item.tablePath) {
          pathParams[LOANS_ROUTING_PARAMS.subtab] = item.tablePath;
          pathParams[LOANS_ROUTING_PARAMS.entityId] = ALL_BORROWERS_PATH;
          pathParams[LOANS_ROUTING_PARAMS.section] = FINANCIAL_INFO_ACTION_TYPE.EDIT;
          pathParams[LOANS_ROUTING_PARAMS.subsection] = item.recordId;
        }

        history.push(LoansPaths.applicationManagement.generatePath(pathParams));

        if (item.isRecordField && item.fieldId) {
          focusAndHighlightField({ fieldId: item.fieldId });
        }
      }
    },
    [applicationId, history, onClose, addRecentItem],
  );

  const { selectedIndex, setSelectedIndex, onKeyDown, resetSelection } = useKeyboardNavigation({
    flatResults: navigableItems,
    onSelect: handleSelect,
    onClose,
  });

  const handleInputChange = useCallback(
    event => {
      const { value } = event.target;
      setInputValue(value);
      onQueryChange(value);
      resetSelection();
    },
    [onQueryChange, resetSelection],
  );

  useEffect(() => {
    if (isOpen) {
      prefetchFinancialInfo();

      if (initialQuery) {
        setInputValue(initialQuery);
        setQuery(initialQuery);
      }
    }

    if (!isOpen) {
      setInputValue('');
      setQuery('');
      resetSelection();
    }
  }, [isOpen, initialQuery, setQuery, resetSelection, prefetchFinancialInfo]);

  useAutofocusSearchOnOpen({ isOpen, inputRef });

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <Backdrop open sx={backdropStyles} onClick={onClose} />
      <Box sx={paletteContainerStyles} onKeyDown={onKeyDown}>
        <Typography component="div" sx={searchInputWrapperStyles} variant="regular14">
          <SearchIcon size={IconSize[20]} style={searchIconStyles} />
          <InputBase
            fullWidth
            inputRef={inputRef}
            placeholder={SEARCH_INPUT_PLACEHOLDER}
            sx={searchInputStyles}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
          />
          <Typography component="span" sx={escHintStyles} variant="medium12">
            Esc
          </Typography>
        </Typography>

        <SearchResults
          flatResults={flatResults}
          flatSuggestedItems={flatSuggestedItems}
          groupedResults={groupedResults}
          query={query}
          recentItems={recentItems}
          selectedIndex={selectedIndex}
          shortcutEntries={shortcutEntries}
          suggestedItems={suggestedItems}
          onClearRecent={clearRecentItems}
          onHoverItem={setSelectedIndex}
          onSelect={handleSelect}
        />

        <Box sx={footerStyles}>
          <Typography sx={footerHintStyles} variant="regular12">
            <Typography component="span" sx={footerKeyStyles} variant="bold12">
              ↑↓
            </Typography>
            Navigate
          </Typography>
          <Typography sx={footerHintStyles} variant="regular12">
            <Typography component="span" sx={footerKeyStyles} variant="bold12">
              ↵
            </Typography>
            Select
          </Typography>
          <Typography sx={footerHintStyles} variant="regular12">
            <Typography component="span" sx={footerKeyStyles} variant="bold12">
              Esc
            </Typography>
            Close
          </Typography>
        </Box>
      </Box>
    </Portal>
  );
};

UniversalSearch.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialQuery: PropTypes.string,
};

export default memo(UniversalSearch);
